<script>
  import { onMount } from 'svelte';
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  import { determineImageUrl } from '../../routes/design/[productSlug]/_design-functions.js';

  import { konvaStageId } from '../../stores/Preview.js';
  import { designState, newProductColour, newProductSublimationTemplate } from '../../stores/Designer.js';
  import { productDataStore } from '../../stores/ProductData.js';

  import Modal from '../Modal.svelte';
  import LoadingSpinner from '../LoadingSpinner.svelte';
  import Button from '../Button.svelte';

  // Props
  export let showModal;
  export let previousDesigns = [];

  // Local reactive variables
  let previewImages = [];
  let previousDesignSummary = [];
  let selectedOption = 'finish';

  // For new product
  let newColour = $newProductColour;
  let newSublimationTemplate = $newProductSublimationTemplate;
  let type = $productDataStore?.showing;
  let newProductBackgroundImageUrl = '';

  /**
   * GraphQL to get design preview
   */
  const DESIGNPREVIEW = gql`
    query MyQuery($backgroundImageUrl: String!, $designElements: jsonb!) {
      designPreview(designElements: $designElements, backgroundImageUrl: $backgroundImageUrl) {
        image
      }
    }
  `;

  /**
   * Query function for design previews
   */
  async function queryDesignPreview(backgroundImageUrl, designElements) {
    const res = await client.query({
      query: DESIGNPREVIEW,
      variables: { backgroundImageUrl, designElements },
    });
    return res.data.designPreview.image;
  }

  /**
   * Generate new product background image URL (async)
   */
  async function getNewProductBackgroundImageUrl() {
    const params = {
      side: 'front',
      showModel: true,
      size: 256,
      fileType: 'png',
      type,
      isModelBasedImages: $productDataStore[type].product?.isModelBasedImages,
    };

    if (newColour) {
      params.newColour = newColour;
    }
    if (newSublimationTemplate) {
      params.newSublimationTemplate = newSublimationTemplate;
    }
    return await determineImageUrl(params);
  }

  /**
   * Generate preview images for:
   * 1) each previous design in its original product
   * 2) each previous design on the new product
   */
  async function generateImages() {
    // 1) Original product previews
    const originalPreviews = previousDesigns.map(async (design) => {
      const productType = design.type;

      const bgUrl = await designState.getProductBackgroundImageUrl({
        designId: $konvaStageId,
        productType,
        side: 'front',
        showModel: true,
        isModelBasedImages: design.product?.product?.isModelBasedImages,
      });

      const designElements = JSON.stringify(design.product.designElements.front);
      return queryDesignPreview(bgUrl, designElements);
    });

    // 2) New product previews (previous design but with new product background)
    const newProductPreviews = previousDesigns.map(() => {
      const designElements = JSON.stringify(previousDesigns[0].product.designElements.front);
      return queryDesignPreview(newProductBackgroundImageUrl, designElements);
    });

    return await Promise.all([...originalPreviews, ...newProductPreviews]);
  }

  /**
   * Create a summary array of previous designs with their images
   */
  function createPreviousDesignSummary(images) {
    // The first half of the images are "original",
    // the second half are "new product" versions
    const halfIndex = previousDesigns.length;

    // Build summary for the original designs
    previousDesignSummary = previousDesigns.map((design, idx) => {
      return {
        id: design.id,
        slug: design.product.product.slug,
        name: design.product.product.name,
        image: images[idx], // original product preview
      };
    });

    // Save out all preview images globally
    previewImages = images;

    // We only need the "new product" portion for `newProductImages`
    // but you can slice as needed. Example:
    // let newProductImages = previewImages.slice(halfIndex);
  }

  /**
   * Handle proceed button click
   */
  function selectDesign() {
    switch (selectedOption) {
      case 'finish':
        // Reload at previous product URL
        const slug = previousDesignSummary[0].slug;
        window.location.href = `/design/${slug}/choose-colours`;
        break;

      case 'change':
        // Change product, keep old design elements
        const productType = $productDataStore.showing;
        let productDetails = $productDataStore[productType];
        let colour = newColour || productDetails.selectedColour;
        let sublimationTemplate = newSublimationTemplate || productDetails.selectedSublimationTemplate;

        // Add product to designState
        designState.addProduct($konvaStageId, productDetails, colour, sublimationTemplate);

        // Remove other products that aren't productType
        ['top', 'bottom', 'hat', 'socks', 'accessory'].forEach((p) => {
          if (p !== productType) {
            designState.deleteProduct($konvaStageId, p);
          }
        });
        location.reload();
        break;

      case 'new':
        // New product entirely, remove all old design elements
        const newType = $productDataStore.showing;
        let newProductDetails = $productDataStore[newType];
        let newCol = newColour || newProductDetails.selectedColour;
        let newSubTemplate = newSublimationTemplate || newProductDetails.selectedSublimationTemplate;
        designState.addProduct($konvaStageId, newProductDetails, newCol, newSubTemplate);

        // Remove any design elements for this productType
        designState.removeAllDesignElements($konvaStageId, newType);

        location.reload();
        break;
    }
  }

  /**
   * When the modal is shown, do these steps:
   * 1) Get new product background image (async)
   * 2) Generate previews
   * 3) Create summary
   */
  $: if (showModal) {
    (async () => {
      // Prevent re-running if we already have data
      // (Optional check if you'd like)
      if (!previousDesignSummary.length) {
        newProductBackgroundImageUrl = await getNewProductBackgroundImageUrl();
        const images = await generateImages();
        createPreviousDesignSummary(images);
      }
    })();
  }
</script>

<Modal show={showModal} allowClose={false}>
  <div class="container">
    {#if previousDesignSummary.length > 0}
      <div class="heading">Previous Design Detected</div>
      <div class="message">
        <p>
          You are loading a <b>New Product</b>, but you have an
          <b>Unfinished Design</b>.
          <br />
          Please choose one of the options below to proceed:
        </p>
      </div>
      <div class="options-container">
        <!-- We only show the first previous design for now, 
             but you could iterate if you wanted to handle multiple. -->
        {#each previousDesignSummary as prev}
          <div class="previous-design">
            <div class="previous-design-heading">Unfinished Design</div>
            <img src={prev.image} alt="" />
            <div class="label">{prev.name}</div>
          </div>
        {/each}

        <div class="options">
          <div class="previous-design-heading">Options</div>
          <div class="options-grid">
            <!-- FINISH -->
            <div
              class="option"
              class:selected={selectedOption === 'finish'}
              on:click={() => (selectedOption = 'finish')}
            >
              <div class="option-content">
                <img class="option-image" src={previousDesignSummary[0]?.image} alt="" />
                <div class="option-description">
                  <div class="description-heading">Finish Previous Design</div>
                  <div class="description-content">Reload your previous design.</div>
                </div>
              </div>
            </div>

            <!-- CHANGE -->
            <div
              class="option"
              class:selected={selectedOption === 'change'}
              on:click={() => (selectedOption = 'change')}
            >
              <div class="option-content">
                <!-- Show the first "new product" preview from previewImages
                     which would be at index [previousDesigns.length] if you want. -->
                <img class="option-image" src={previewImages[previousDesigns.length]} alt="" />
                <div class="option-description">
                  <div class="description-heading">Change Product</div>
                  <div class="description-content">Reload your previous design on this new product.</div>
                </div>
              </div>
            </div>

            <!-- NEW -->
            <div class="option" class:selected={selectedOption === 'new'} on:click={() => (selectedOption = 'new')}>
              <div class="option-content">
                <img class="option-image" src={newProductBackgroundImageUrl} alt="" />
                <div class="option-description">
                  <div class="description-heading">Start New Design</div>
                  <div class="description-content">This will remove the previous design.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button">
          <Button label="Proceed" clickFn={selectDesign} />
        </div>
      </div>
    {:else}
      <!-- No previous design summary yet => show spinner -->
      <div class="spinner">
        <LoadingSpinner />
      </div>
    {/if}
  </div>
</Modal>

<style>
  .container {
    min-width: 30vw;
    min-height: 30vh;
  }

  .heading {
    font-family: 'Oswald';
    text-transform: uppercase;
    color: #fe5900;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }

  .message {
    margin-top: 1rem;
    font-size: 1.4rem;
  }

  .options-container {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    grid-gap: 1rem;
  }

  .previous-design {
    margin-top: 1rem;
    padding: 1rem;
  }

  .previous-design img {
    margin-top: 2rem;
    max-width: 100%;
  }

  .previous-design-heading {
    text-transform: capitalize;
    font-family: 'Oswald';
    color: #fe5900;
    margin-bottom: 0.5rem;
    text-align: center;
  }

  .options {
    margin-top: 1rem;
    padding: 1rem;
  }

  .option {
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    cursor: pointer;
  }

  .selected {
    border: 2px solid #fe5900;
  }

  .option-content {
    height: 100%;
    display: flex;
  }

  .options-grid {
    display: grid;
    grid-template-rows: repeat(3, minmax(0, 100px));
    grid-gap: 1rem;
  }

  .option-image {
    align-self: flex-start;
    max-width: 180px;
    max-height: 100%;
    height: auto;
    width: auto;
    border-right: 1px solid rgba(0, 0, 0, 0.15);
    padding: 5px;
  }

  .option-description {
    width: 100%;
  }

  .description-heading {
    font-size: 1.3rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    padding: 0.5rem;
  }

  .description-content {
    padding-left: 0.5rem;
    font-size: 1.2rem;
    padding: 0.5rem;
  }

  .label {
    text-transform: capitalize;
    font-weight: bold;
    font-size: 1.4rem;
    text-align: center;
  }

  .button {
    margin-left: auto;
    margin-right: 1rem;
  }

  .spinner {
    height: 30vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media screen and (max-width: 700px) {
    .options-container {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
</style>
